import { useEffect, useState } from 'react';
import Widget from '../../../components/Dashboard/Widget';
import { useDashboardControls } from 'contexts/Dashboard/useDashboardControls';
import { KPIsWidgetEnum, LongTitles } from '../utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useKPIGetDeliveryConditionByBooker } from 'api/kpi/kpi';
import { KPIDeliveryConditionDataPoint, KPIGroupBy } from 'api/model';
import { maxKPIDateRangeMs } from 'constants/AppConstants';

type SortField =
  | 'groupName'
  | 'totalInspections'
  | 'topTierPercentage'
  | 'middleTierPercentage'
  | 'bottomTierPercentage'
  | 'averageRating';
type SortDirection = 'asc' | 'desc';

const SortIndicator = ({
  field,
  sortField,
  sortDirection,
}: {
  field: SortField;
  sortField: SortField;
  sortDirection: SortDirection;
}) => {
  if (sortField !== field) return null;
  return <span>{sortDirection === 'asc' ? ' ▲' : ' ▼'}</span>;
};

const DeliveryConditionWidget = () => {
  const [groupBy, setGroupBy] = useState<KPIGroupBy>(KPIGroupBy.Booker);
  const [sortField, setSortField] = useState<SortField>('bottomTierPercentage');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const {
    dashboardState: {
      daterange,
      selectedSupplierIDs,
      selectedCustomerNames,
      selectedFromCountyIDs,
      selectedToCountyIDs,
      excludeCompanyNameContains,
    },
  } = useDashboardControls();

  const getKPIDeliveryConditionData = useKPIGetDeliveryConditionByBooker();

  const startDate = daterange[0];
  const endDate = daterange[1];

  const startDateTime = startDate ? startDate.getTime() : null;
  const endDateTime = endDate ? endDate.getTime() : null;

  const isDateRangeTooLong =
    startDate &&
    endDate &&
    endDate.getTime() - startDate.getTime() > maxKPIDateRangeMs;

  useEffect(() => {
    if (startDate && endDate && !isDateRangeTooLong) {
      getKPIDeliveryConditionData.mutate({
        data: {
          startDate,
          endDate,
          supplierIDs: selectedSupplierIDs,
          customerNames: selectedCustomerNames,
          fromCountyIDs: selectedFromCountyIDs,
          toCountyIDs: selectedToCountyIDs,
          kpiGroupBy: groupBy,
          excludeCompanyNameContains: excludeCompanyNameContains ?? undefined,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    startDateTime,
    endDateTime,
    selectedSupplierIDs,
    selectedCustomerNames,
    selectedFromCountyIDs,
    selectedToCountyIDs,
    groupBy,
    excludeCompanyNameContains,
    isDateRangeTooLong,
  ]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortData = (
    data: KPIDeliveryConditionDataPoint[]
  ): KPIDeliveryConditionDataPoint[] => {
    return [...data].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      switch (sortField) {
        case 'groupName':
          aVal = a.groupName || '';
          bVal = b.groupName || '';
          break;
        case 'totalInspections':
          aVal = a.totalInspections;
          bVal = b.totalInspections;
          break;
        case 'topTierPercentage':
          aVal = a.topTierPercentage;
          bVal = b.topTierPercentage;
          break;
        case 'middleTierPercentage':
          aVal = a.middleTierPercentage;
          bVal = b.middleTierPercentage;
          break;
        case 'bottomTierPercentage':
          aVal = a.bottomTierPercentage;
          bVal = b.bottomTierPercentage;
          break;
        case 'averageRating':
          aVal = a.averageRating;
          bVal = b.averageRating;
          break;
        default:
          return 0;
      }

      if (typeof aVal === 'string') {
        const comparison = aVal.localeCompare(bVal as string);
        return sortDirection === 'asc' ? comparison : -comparison;
      }
      return sortDirection === 'asc'
        ? aVal - (bVal as number)
        : (bVal as number) - aVal;
    });
  };

  const result = getKPIDeliveryConditionData.data;
  const currentData = result?.dataPoints || [];
  const sortedData = sortData(currentData);

  const groupLabel = groupBy === KPIGroupBy.Company ? 'Företag' : 'Säljare';
  const topName = result?.topTier?.name ?? 'Utställning';
  const midName = result?.middleTier?.name ?? 'Godtagbart';
  const botName = result?.bottomTier?.name ?? 'Ej godtagbart';

  return (
    <Widget
      title={LongTitles[KPIsWidgetEnum.DeliveryCondition]}
      count={undefined}
      isLoading={getKPIDeliveryConditionData.isLoading}
      info="Andel inspektioner per leveransskickskategori. Baserat på ärendets stängd-datum."
    >
      <p style={{ fontWeight: 'bold', textAlign: 'center' }}>
        OBS: Datumen utgår från ärendets stängd-datum.
      </p>

      <div style={{ display: 'flex', marginBottom: '15px' }}>
        <button
          type="button"
          onClick={() => setGroupBy(KPIGroupBy.Booker)}
          style={{
            padding: '8px 16px',
            cursor: 'pointer',
            backgroundColor:
              groupBy === KPIGroupBy.Booker ? '#007bff' : '#f0f0f0',
            color: groupBy === KPIGroupBy.Booker ? 'white' : 'black',
            border: '1px solid #ccc',
            borderRadius: '4px 0 0 4px',
          }}
        >
          Per säljare
        </button>
        <button
          type="button"
          onClick={() => setGroupBy(KPIGroupBy.Company)}
          style={{
            padding: '8px 16px',
            cursor: 'pointer',
            backgroundColor:
              groupBy === KPIGroupBy.Company ? '#007bff' : '#f0f0f0',
            color: groupBy === KPIGroupBy.Company ? 'white' : 'black',
            border: '1px solid #ccc',
            borderLeft: 'none',
            borderRadius: '0 4px 4px 0',
          }}
        >
          Per företag
        </button>
      </div>

      {isDateRangeTooLong ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <p>
            Datumintervallet är för långt för att visa data. Använd ett kortare
            intervall.
          </p>
        </div>
      ) : sortedData.length === 0 ? (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          {getKPIDeliveryConditionData.isLoading ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : (
            <p>Ingen data att visa för vald filtrering.</p>
          )}
        </div>
      ) : (
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead
              style={{ position: 'sticky', top: 0, backgroundColor: 'white' }}
            >
              <tr>
                <th
                  onClick={() => handleSort('groupName')}
                  style={{
                    cursor: 'pointer',
                    padding: '8px',
                    textAlign: 'left',
                    borderBottom: '2px solid #ddd',
                  }}
                >
                  {groupLabel}
                  <SortIndicator
                    field="groupName"
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                </th>
                <th
                  onClick={() => handleSort('totalInspections')}
                  style={{
                    cursor: 'pointer',
                    padding: '8px',
                    textAlign: 'right',
                    borderBottom: '2px solid #ddd',
                  }}
                >
                  Totalt
                  <SortIndicator
                    field="totalInspections"
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                </th>
                <th
                  onClick={() => handleSort('topTierPercentage')}
                  style={{
                    cursor: 'pointer',
                    padding: '8px',
                    textAlign: 'right',
                    borderBottom: '2px solid #ddd',
                  }}
                  title={topName}
                >
                  {topName}
                  <SortIndicator
                    field="topTierPercentage"
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                </th>
                <th
                  onClick={() => handleSort('middleTierPercentage')}
                  style={{
                    cursor: 'pointer',
                    padding: '8px',
                    textAlign: 'right',
                    borderBottom: '2px solid #ddd',
                  }}
                  title={midName}
                >
                  {midName}
                  <SortIndicator
                    field="middleTierPercentage"
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                </th>
                <th
                  onClick={() => handleSort('bottomTierPercentage')}
                  style={{
                    cursor: 'pointer',
                    padding: '8px',
                    textAlign: 'right',
                    borderBottom: '2px solid #ddd',
                  }}
                  title={botName}
                >
                  {botName}
                  <SortIndicator
                    field="bottomTierPercentage"
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                </th>
                <th
                  onClick={() => handleSort('averageRating')}
                  style={{
                    cursor: 'pointer',
                    padding: '8px',
                    textAlign: 'right',
                    borderBottom: '2px solid #ddd',
                  }}
                >
                  Snittbetyg
                  <SortIndicator
                    field="averageRating"
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((dp, index) => (
                <tr
                  key={dp.groupName || index}
                  style={{ borderBottom: '1px solid #eee' }}
                >
                  <td style={{ padding: '8px' }}>
                    {dp.groupName || '(Ingen)'}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>
                    {dp.totalInspections}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>
                    {dp.topTierPercentage.toFixed(1)}% ({dp.topTierCount})
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>
                    {dp.middleTierPercentage.toFixed(1)}% ({dp.middleTierCount})
                  </td>
                  <td
                    style={{
                      padding: '8px',
                      textAlign: 'right',
                      color: dp.bottomTierPercentage > 0 ? '#c0392b' : undefined,
                      fontWeight: dp.bottomTierPercentage > 0 ? 'bold' : undefined,
                    }}
                  >
                    {dp.bottomTierPercentage.toFixed(1)}% ({dp.bottomTierCount})
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>
                    {dp.averageRating.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Widget>
  );
};

export default DeliveryConditionWidget;
